import React, { Fragment, useEffect, useState, useMemo } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Nav,
  Row,
  Spinner,
  Tab,
  Table
} from "react-bootstrap";
import swal from "sweetalert";
import api, { getFileUrl } from "../../../api";
import PageTitle from "../../layouts/PageTitle";

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const currentYear = new Date().getFullYear();
const years = [currentYear - 1, currentYear, currentYear + 1];

const formatAddress = (addr) => {
  if (!addr) return "";
  if (typeof addr === "string") {
    if (addr === "[object Object]") return "";
    return addr;
  }
  if (typeof addr === "object") {
    const parts = [
      addr.addressLine1,
      addr.addressLine2,
      addr.street,
      addr.locality,
      addr.city,
      addr.state,
      addr.pincode
    ].filter(Boolean);
    return parts.join(", ");
  }
  return String(addr);
};

const ComplianceDossierPage = () => {
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedWoId, setSelectedWoId] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [fetchingStaff, setFetchingStaff] = useState(false);
  const [activeTab, setActiveTab] = useState("cover");

  // View Mode: "editor" or "list"
  const [viewMode, setViewMode] = useState("list");
  const [dossierList, setDossierList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listSearch, setListSearch] = useState("");
  const [listYearFilter, setListYearFilter] = useState("");
  const [listMonthFilter, setListMonthFilter] = useState("");
  const [listStatusFilter, setListStatusFilter] = useState("");

  // Dossier State
  const [dossier, setDossier] = useState(null);
  const [dossierId, setDossierId] = useState(null);

  // Edit / Add Worker Modal
  const [showWorkerModal, setShowWorkerModal] = useState(false);
  const [editingWorkerIndex, setEditingWorkerIndex] = useState(null);
  const [workerFormData, setWorkerFormData] = useState({
    name: "",
    workerIdNo: "",
    fatherName: "",
    designation: "Helper",
    skillLevel: "Skilled",
    rateOfWage: 535,
    daysWorked: 26,
    basic: 13910,
    da: 0,
    hra: 0,
    otherAllowance: 0,
    grossWages: 13910,
    pfEmployee: 1669,
    esicEmployee: 0,
    pt: 200,
    advance: 0,
    totalDeductions: 1869,
    netPayable: 12041,
    employerPfShare: 1669,
    paymentDate: "",
    utrNumber: "",
    receiptMode: "Bank Transfer"
  });

  // Fetch Work Orders and Dossier List on Mount
  useEffect(() => {
    fetchWorkOrders();
    fetchDossierList();
  }, []);

  const fetchDossierList = async () => {
    setLoadingList(true);
    try {
      const res = await api.get("/compliance-dossiers");
      if (res.data.success) {
        setDossierList(res.data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch dossier list:", err);
    } finally {
      setLoadingList(false);
    }
  };

  const fetchWorkOrders = async () => {
    try {
      const res = await api.get("/work-orders");
      const list = res.data.data || res.data || [];
      setWorkOrders(list);
      if (list.length > 0 && !selectedWoId) {
        setSelectedWoId(list[0]._id);
      }
    } catch (err) {
      console.error("Failed to load work orders:", err);
    }
  };

  // Load or Initialize Dossier when WO / Month / Year changes
  useEffect(() => {
    if (selectedWoId) {
      loadDossier();
    }
  }, [selectedWoId, selectedMonth, selectedYear]);

  const loadDossier = async () => {
    setLoading(true);
    try {
      const res = await api.get("/compliance-dossiers/init", {
        params: {
          workOrderId: selectedWoId,
          month: selectedMonth,
          year: selectedYear
        }
      });
      if (res.data.success) {
        const d = res.data.data;
        if (d) {
          d.clientAddress = formatAddress(d.clientAddress);
          d.clientCity = typeof d.clientCity === "object" ? "" : (d.clientCity || "");
          d.clientState = typeof d.clientState === "object" ? "" : (d.clientState || "");
          d.clientPincode = typeof d.clientPincode === "object" ? "" : (d.clientPincode || "");
        }
        setDossier(d);
        setDossierId(d?._id || null);
      }
    } catch (err) {
      console.error("Error loading dossier:", err);
      swal("Error", err.response?.data?.message || "Failed to load dossier", "error");
    } finally {
      setLoading(false);
    }
  };

  // Save Dossier
  const handleSave = async (showSuccessAlert = true) => {
    if (!dossier) return;
    setSaving(true);
    try {
      const payload = {
        ...dossier,
        workOrderId: selectedWoId,
        month: selectedMonth,
        year: selectedYear
      };
      const res = await api.post("/compliance-dossiers", payload);
      if (res.data.success) {
        setDossier(res.data.data);
        setDossierId(res.data.data._id);
        if (showSuccessAlert) {
          swal("Success", "Compliance Dossier saved successfully!", "success");
        }
      }
    } catch (err) {
      console.error("Error saving dossier:", err);
      swal("Error", err.response?.data?.message || "Failed to save dossier", "error");
    } finally {
      setSaving(false);
    }
  };

  // Download Dossier PDF
  const handleDownloadPdf = async () => {
    let idToUse = dossierId || dossier?._id;
    if (!idToUse) {
      await handleSave(false);
      idToUse = dossierId || dossier?._id;
    }
    if (!idToUse) {
      swal("Info", "Please save the dossier before downloading PDF.", "info");
      return;
    }

    setDownloadingPdf(true);
    try {
      const res = await api.get(`/compliance-dossiers/${idToUse}/pdf`, {
        responseType: "blob"
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");

      const monthStr = months[dossier.month] || "Month";
      const filename = `Compliance_Dossier_${dossier.projectName || "Site"}_${monthStr}_${dossier.year}.pdf`
        .replace(/[^a-zA-Z0-9_\-\.]/g, "_");
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error("Failed to generate PDF:", err);
      swal("Error", "Failed to generate / download PDF dossier.", "error");
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Download Form B Excel
  const handleDownloadExcel = async () => {
    let idToUse = dossierId || dossier?._id;
    if (!idToUse) {
      await handleSave(false);
      idToUse = dossierId || dossier?._id;
    }
    if (!idToUse) {
      swal("Info", "Please save the dossier before downloading Excel.", "info");
      return;
    }

    setDownloadingExcel(true);
    try {
      const res = await api.get(`/compliance-dossiers/${idToUse}/excel`, {
        responseType: "blob"
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
      const blobUrl = window.URL.createObjectURL(blob);
      const monthStr = months[dossier.month] || "Month";
      const filename = `Form_B_Wage_Register_${monthStr}_${dossier.year}.xlsx`
        .replace(/[^a-zA-Z0-9_\-\.]/g, "_");
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error("Failed to download Excel:", err);
      swal("Error", "Failed to generate Form B Excel file.", "error");
    } finally {
      setDownloadingExcel(false);
    }
  };

  // File Upload Helper for Attached Docs
  const handleFileUpload = async (e, docType, defaultTitle) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64Data = reader.result;
      try {
        const res = await api.post("/compliance-dossiers/upload-doc", {
          base64Data,
          docType,
          title: defaultTitle || file.name
        });
        if (res.data.success) {
          const newDoc = res.data.data;
          const updatedDocs = [...(dossier?.attachedDocuments || []), newDoc];
          setDossier((prev) => ({
            ...prev,
            attachedDocuments: updatedDocs
          }));

          // Automatically persist updated attachedDocuments
          const payload = {
            ...dossier,
            workOrderId: selectedWoId,
            month: selectedMonth,
            year: selectedYear,
            attachedDocuments: updatedDocs
          };
          const saveRes = await api.post("/compliance-dossiers", payload);
          if (saveRes.data.success) {
            setDossier(saveRes.data.data);
            setDossierId(saveRes.data.data._id);
          }
          swal("Uploaded", `${defaultTitle} attached successfully!`, "success");
        }
      } catch (err) {
        swal("Error", "Failed to upload document.", "error");
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveDoc = async (index) => {
    const updatedDocs = (dossier.attachedDocuments || []).filter((_, i) => i !== index);
    setDossier((prev) => ({ ...prev, attachedDocuments: updatedDocs }));
    try {
      const payload = {
        ...dossier,
        workOrderId: selectedWoId,
        month: selectedMonth,
        year: selectedYear,
        attachedDocuments: updatedDocs
      };
      const saveRes = await api.post("/compliance-dossiers", payload);
      if (saveRes.data.success) {
        setDossier(saveRes.data.data);
        setDossierId(saveRes.data.data._id);
      }
    } catch (err) {
      console.error("Failed to remove document:", err);
    }
  };

  // Handle Wage Register Calculations
  const updateWorkerItem = (index, updatedItem) => {
    const list = [...(dossier.wageRegister || [])];
    list[index] = updatedItem;
    setDossier((prev) => ({ ...prev, wageRegister: list }));
  };

  const removeWorkerItem = (index) => {
    const list = (dossier.wageRegister || []).filter((_, idx) => idx !== index);
    const muster = (dossier.musterRoll || []).filter((_, idx) => idx !== index);
    setDossier((prev) => ({ ...prev, wageRegister: list, musterRoll: muster }));
  };

  const openAddWorkerModal = () => {
    setEditingWorkerIndex(null);
    setWorkerFormData({
      name: "",
      workerIdNo: `W-${String((dossier?.wageRegister?.length || 0) + 1).padStart(4, "0")}`,
      fatherName: "",
      designation: "Helper",
      skillLevel: "Skilled",
      rateOfWage: 535,
      daysWorked: 26,
      basic: 13910,
      da: 0,
      hra: 0,
      otherAllowance: 0,
      grossWages: 13910,
      pfEmployee: 1669,
      esicEmployee: 0,
      pt: 200,
      advance: 0,
      totalDeductions: 1869,
      netPayable: 12041,
      employerPfShare: 1669,
      paymentDate: "",
      utrNumber: "",
      receiptMode: "Bank Transfer"
    });
    setShowWorkerModal(true);
  };

  const openEditWorkerModal = (item, index) => {
    setEditingWorkerIndex(index);
    setWorkerFormData({ ...item });
    setShowWorkerModal(true);
  };

  const handleWorkerFormSave = () => {
    if (!workerFormData.name) {
      swal("Error", "Please enter worker name", "warning");
      return;
    }

    const reg = [...(dossier?.wageRegister || [])];
    const muster = [...(dossier?.musterRoll || [])];

    if (editingWorkerIndex !== null) {
      reg[editingWorkerIndex] = { ...workerFormData };
      if (muster[editingWorkerIndex]) {
        muster[editingWorkerIndex].name = workerFormData.name;
        muster[editingWorkerIndex].designation = workerFormData.designation;
        muster[editingWorkerIndex].totalDaysWorked = workerFormData.daysWorked;
      }
    } else {
      reg.push({ ...workerFormData, serialNo: reg.length + 1 });
      const dailyAttendance = {};
      for (let d = 1; d <= 31; d++) {
        dailyAttendance[String(d)] = d <= workerFormData.daysWorked ? (d % 7 === 0 ? "WO" : "P") : "A";
      }
      muster.push({
        serialNo: muster.length + 1,
        name: workerFormData.name,
        dob: "01/01/1995",
        dateOfEntry: "01/01/2024",
        designation: workerFormData.designation,
        dailyAttendance,
        totalDaysWorked: workerFormData.daysWorked
      });
    }

    setDossier((prev) => ({ ...prev, wageRegister: reg, musterRoll: muster }));
    setShowWorkerModal(false);
  };

  // Calculations for Totals
  const totals = useMemo(() => {
    const list = dossier?.wageRegister || [];
    return list.reduce(
      (acc, row) => {
        acc.daysWorked += Number(row.daysWorked || 0);
        acc.grossWages += Number(row.grossWages || 0);
        acc.pfEmployee += Number(row.pfEmployee || 0);
        acc.esicEmployee += Number(row.esicEmployee || 0);
        acc.pt += Number(row.pt || 0);
        acc.advance += Number(row.advance || 0);
        acc.totalDeductions += Number(row.totalDeductions || 0);
        acc.netPayable += Number(row.netPayable || 0);
        acc.employerPfShare += Number(row.employerPfShare || 0);
        return acc;
      },
      {
        daysWorked: 0,
        grossWages: 0,
        pfEmployee: 0,
        esicEmployee: 0,
        pt: 0,
        advance: 0,
        totalDeductions: 0,
        netPayable: 0,
        employerPfShare: 0
      }
    );
  }, [dossier?.wageRegister]);

  // Auto-fetch staff assigned to Work Order and Tasks
  const handleAutoFetchStaff = async () => {
    if (!selectedWoId) {
      swal("Info", "Please select a Work Order first.", "info");
      return;
    }
    setFetchingStaff(true);
    try {
      const res = await api.get("/compliance-dossiers/fetch-staff", {
        params: {
          workOrderId: selectedWoId,
          month: selectedMonth,
          year: selectedYear
        }
      });
      if (res.data.success) {
        const fetched = res.data.data || [];
        if (fetched.length === 0) {
          swal("No Staff Found", "No staff members or tasks assigned to this Work Order were found.", "info");
        } else {
          setDossier((prev) => ({
            ...prev,
            projectStaff: fetched
          }));
          swal("Staff Loaded", `Successfully loaded ${fetched.length} assigned staff member(s) with their task assignments!`, "success");
        }
      }
    } catch (err) {
      console.error("Failed to fetch staff:", err);
      swal("Error", err.response?.data?.message || "Failed to auto-fetch staff", "error");
    } finally {
      setFetchingStaff(false);
    }
  };

  // Open a dossier from the list into the editor
  const handleOpenDossierFromList = (item) => {
    if (item.workOrderId?._id || item.workOrderId) {
      setSelectedWoId(item.workOrderId?._id || item.workOrderId);
    }
    if (item.month !== undefined) {
      setSelectedMonth(item.month);
    }
    if (item.year) {
      setSelectedYear(item.year);
    }
    setViewMode("editor");
  };

  // Delete a dossier
  const handleDeleteDossier = async (id, title) => {
    const willDelete = await swal({
      title: "Are you sure?",
      text: `Do you want to delete compliance dossier "${title || id}"? This cannot be undone.`,
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    });
    if (!willDelete) return;

    try {
      const res = await api.delete(`/compliance-dossiers/${id}`);
      if (res.data.success) {
        swal("Deleted!", "Compliance dossier deleted successfully.", "success");
        fetchDossierList();
        if (dossierId === id) {
          loadDossier();
        }
      }
    } catch (err) {
      console.error("Delete failed:", err);
      swal("Error", err.response?.data?.message || "Failed to delete dossier.", "error");
    }
  };

  // Download PDF for list item
  const handleDownloadPdfForDossier = async (id, projectName, month, year) => {
    try {
      const res = await api.get(`/compliance-dossiers/${id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const blobUrl = window.URL.createObjectURL(blob);
      const monthStr = months[month] || "Month";
      const filename = `Compliance_Dossier_${projectName || "Site"}_${monthStr}_${year}.pdf`
        .replace(/[^a-zA-Z0-9_\-\.]/g, "_");
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error("Failed to download PDF:", err);
      swal("Error", "Failed to download PDF.", "error");
    }
  };

  // Download Excel for list item
  const handleDownloadExcelForDossier = async (id, month, year) => {
    try {
      const res = await api.get(`/compliance-dossiers/${id}/excel`, { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
      const blobUrl = window.URL.createObjectURL(blob);
      const monthStr = months[month] || "Month";
      const filename = `Form_B_Wage_Register_${monthStr}_${year}.xlsx`
        .replace(/[^a-zA-Z0-9_\-\.]/g, "_");
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error("Failed to download Excel:", err);
      swal("Error", "Failed to generate Form B Excel file.", "error");
    }
  };

  // Filtered Dossiers List
  const filteredDossiers = useMemo(() => {
    return (dossierList || []).filter((d) => {
      if (listYearFilter && String(d.year) !== String(listYearFilter)) return false;
      if (listMonthFilter !== "" && String(d.month) !== String(listMonthFilter)) return false;
      if (listStatusFilter && d.status !== listStatusFilter) return false;
      if (listSearch) {
        const q = listSearch.toLowerCase();
        const clientName = (d.clientName || d.workOrderId?.client?.companyName || d.workOrderId?.client?.name || "").toLowerCase();
        const projectName = (d.projectName || d.workOrderId?.subject || "").toLowerCase();
        const woNo = (d.workOrderId?.woNo || "").toLowerCase();
        const trrn = (d.epfo?.trrn || "").toLowerCase();
        if (!clientName.includes(q) && !projectName.includes(q) && !woNo.includes(q) && !trrn.includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [dossierList, listSearch, listYearFilter, listMonthFilter, listStatusFilter]);

  return (
    <Fragment>
      <PageTitle
        activeMenu="Statutory Compliance Dossier"
        motherMenu="HR &amp; Compliance"
      />

      {/* HEADER BAR */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h5 className="mb-0 fw-bold">
          {viewMode === "list" ? (
            <><i className="fas fa-folder-open me-2 text-primary"></i>Statutory Compliance Dossiers</>
          ) : (
            <><i className="fas fa-file-alt me-2 text-primary"></i>Dossier Generator / Editor</>
          )}
        </h5>
        <div>
          {viewMode === "list" ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setViewMode("editor")}
            >
              <i className="fas fa-plus me-1"></i> Add New Dossier
            </Button>
          ) : (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => { setViewMode("list"); fetchDossierList(); }}
            >
              <i className="fas fa-arrow-left me-1"></i> Back to List
            </Button>
          )}
        </div>
      </div>

      {viewMode === "list" ? (
        /* ══════════════════════════════════════════════════════════
             VIEW: ALL SUBMISSIONS / DOSSIER LIST
        ══════════════════════════════════════════════════════════ */
        <Card className="shadow-sm border-0 mb-4">
          <Card.Header className="bg-white py-3 border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h5 className="mb-0 fw-bold text-primary">
              <i className="fas fa-folder-open me-2"></i> Statutory Compliance Submissions History
            </h5>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={fetchDossierList}
              disabled={loadingList}
            >
              {loadingList ? (
                <>
                  <Spinner size="sm" animation="border" className="me-1" /> Refreshing...
                </>
              ) : (
                <>
                  <i className="fas fa-sync me-1"></i> Refresh List
                </>
              )}
            </Button>
          </Card.Header>
          <Card.Body>
            {/* List Filters */}
            <Row className="g-3 mb-3 align-items-end">
              <Col lg={4} md={6}>
                <Form.Label className="small fw-semibold text-muted">SEARCH</Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder="Search by Project, Client, WO, TRRN..."
                  value={listSearch}
                  onChange={(e) => setListSearch(e.target.value)}
                />
              </Col>
              <Col lg={2} md={3} sm={6}>
                <Form.Label className="small fw-semibold text-muted">MONTH</Form.Label>
                <Form.Select
                  size="sm"
                  value={listMonthFilter}
                  onChange={(e) => setListMonthFilter(e.target.value)}
                >
                  <option value="">All Months</option>
                  {months.map((m, idx) => (
                    <option key={idx} value={idx}>
                      {m}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col lg={2} md={3} sm={6}>
                <Form.Label className="small fw-semibold text-muted">YEAR</Form.Label>
                <Form.Select
                  size="sm"
                  value={listYearFilter}
                  onChange={(e) => setListYearFilter(e.target.value)}
                >
                  <option value="">All Years</option>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col lg={2} md={3} sm={6}>
                <Form.Label className="small fw-semibold text-muted">STATUS</Form.Label>
                <Form.Select
                  size="sm"
                  value={listStatusFilter}
                  onChange={(e) => setListStatusFilter(e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value="DRAFT">DRAFT</option>
                  <option value="SUBMITTED">SUBMITTED</option>
                </Form.Select>
              </Col>
              <Col lg={2} md={3} sm={6}>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="w-100"
                  onClick={() => {
                    setListSearch("");
                    setListMonthFilter("");
                    setListYearFilter("");
                    setListStatusFilter("");
                  }}
                >
                  <i className="fas fa-undo me-1"></i> Reset Filters
                </Button>
              </Col>
            </Row>

            {/* List Table */}
            {loadingList ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted small">Loading submissions...</p>
              </div>
            ) : filteredDossiers.length === 0 ? (
              <div className="text-center py-5 border rounded bg-light">
                <i className="fas fa-folder-open fa-3x text-muted mb-3"></i>
                <h6>No Compliance Dossiers Found</h6>
                <p className="text-muted small">
                  {dossierList.length === 0
                    ? "No statutory compliance submissions have been saved yet."
                    : "No records match your selected search or filter criteria."}
                </p>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setViewMode("editor")}
                >
                  <i className="fas fa-plus me-1"></i> Create First Dossier
                </Button>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover size="sm" className="align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "40px" }}>#</th>
                      <th>Project</th>
                      <th>Work Order</th>
                      <th>Client Name</th>
                      <th>Period</th>
                      <th className="text-center">Workers</th>
                      <th className="text-end">Gross Wages (₹)</th>
                      <th className="text-end">Net Pay (₹)</th>
                      <th>EPFO TRRN</th>
                      <th className="text-center">Attachments</th>
                      <th className="text-center">Status</th>
                      <th className="text-center" style={{ minWidth: "180px" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDossiers.map((item, idx) => {
                      const totalGross = (item.wageRegister || []).reduce((acc, w) => acc + (w.grossWages || 0), 0);
                      const totalNet = (item.wageRegister || []).reduce((acc, w) => acc + (w.netPayable || 0), 0);
                      const woNumber = item.workOrderId?.woNo || item.workOrderId?.woNumber || "—";
                      const pName = item.projectName || item.workOrderId?.project?.projectName || item.workOrderId?.subject || "—";
                      const cName = item.clientName || item.workOrderId?.client?.companyName || item.workOrderId?.client?.name || "-";
                      const monthName = months[item.month] || "Month";

                      return (
                        <tr key={item._id || idx}>
                          <td className="fw-semibold text-muted">{idx + 1}</td>
                          <td>
                            <div className="fw-bold text-dark">{pName}</div>
                          </td>
                          <td>
                            <div className="small text-muted">{woNumber}</div>
                          </td>
                          <td className="small">{cName}</td>
                          <td>
                            <Badge bg="light" text="dark" className="border">
                              {monthName} {item.year}
                            </Badge>
                          </td>
                          <td className="text-center fw-semibold">
                            {item.wageRegister?.length || item.musterRoll?.length || 0}
                          </td>
                          <td className="text-end fw-semibold">
                            ₹{totalGross.toLocaleString("en-IN")}
                          </td>
                          <td className="text-end fw-bold text-success">
                            ₹{totalNet.toLocaleString("en-IN")}
                          </td>
                          <td>
                            <span className="small font-monospace">
                              {item.epfo?.trrn || "-"}
                            </span>
                          </td>
                          <td className="text-center">
                            {(item.attachedDocuments || []).length > 0 ? (
                              <Badge bg="info" pill>
                                <i className="fas fa-paperclip me-1"></i>
                                {item.attachedDocuments.length}
                              </Badge>
                            ) : (
                              <span className="text-muted small">0</span>
                            )}
                          </td>
                          <td className="text-center">
                            <Badge bg={item.status === "SUBMITTED" ? "success" : "warning"}>
                              {item.status || "DRAFT"}
                            </Badge>
                          </td>
                          <td className="text-center">
                            <div className="d-flex align-items-center justify-content-center gap-1">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="py-0 px-2"
                                title="Open & Edit Dossier"
                                onClick={() => handleOpenDossierFromList(item)}
                              >
                                <i className="fas fa-edit me-1"></i> Edit
                              </Button>
                              <Button
                                variant="outline-info"
                                size="sm"
                                className="py-0 px-2"
                                title="Download PDF Dossier"
                                onClick={() => handleDownloadPdfForDossier(item._id, pName, item.month, item.year)}
                              >
                                <i className="fas fa-file-pdf"></i>
                              </Button>
                              <Button
                                variant="outline-success"
                                size="sm"
                                className="py-0 px-2"
                                title="Download Form B Excel"
                                onClick={() => handleDownloadExcelForDossier(item._id, item.month, item.year)}
                              >
                                <i className="fas fa-file-excel"></i>
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                className="py-0 px-2"
                                title="Delete Dossier"
                                onClick={() => handleDeleteDossier(item._id, `${pName} (${monthName} ${item.year})`)}
                              >
                                <i className="fas fa-trash"></i>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      ) : (
        /* ══════════════════════════════════════════════════════════
             VIEW: DOSSIER GENERATOR / EDITOR
        ══════════════════════════════════════════════════════════ */
        <>
          {/* TOP FILTERS & ACTION BAR */}
          <Card className="mb-3 shadow-sm border-0">
            <Card.Body className="py-3">
              <Row className="align-items-end g-3">
                <Col lg={3} md={4} sm={12}>
                  <Form.Label className="fw-bold mb-1 small text-muted">
                    SELECT WORK ORDER / PROJECT
                  </Form.Label>
                  <Form.Select
                    value={selectedWoId}
                    onChange={(e) => setSelectedWoId(e.target.value)}
                    className="form-control-sm fw-semibold"
                  >
                    {workOrders.map((wo) => (
                      <option key={wo._id} value={wo._id}>
                        {wo.woNo}{wo.label ? ` – ${wo.label}` : ""} — {wo.client?.companyName || wo.client?.name || "Client"} ({wo.subject || "Project"})
                      </option>
                    ))}
                  </Form.Select>
                </Col>

            <Col lg={2} md={2} sm={6}>
              <Form.Label className="fw-bold mb-1 small text-muted">
                MONTH
              </Form.Label>
              <Form.Select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
                className="form-control-sm"
              >
                {months.map((m, idx) => (
                  <option key={idx} value={idx}>
                    {m}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col lg={2} md={2} sm={6}>
              <Form.Label className="fw-bold mb-1 small text-muted">
                YEAR
              </Form.Label>
              <Form.Select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                className="form-control-sm"
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col lg={5} md={12} sm={12}>
              <div className="d-flex align-items-center justify-content-lg-end gap-2 flex-wrap flex-sm-nowrap">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={loadDossier}
                  disabled={loading}
                >
                  <i className="fas fa-sync me-1"></i> Refresh
                </Button>

                <Button
                  variant="success"
                  size="sm"
                  onClick={() => handleSave(true)}
                  disabled={saving || loading}
                >
                  {saving ? (
                    <>
                      <Spinner size="sm" animation="border" className="me-1" /> Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-1"></i> Save Draft
                    </>
                  )}
                </Button>

                <Button
                  variant="outline-success"
                  size="sm"
                  onClick={handleDownloadExcel}
                  disabled={loading || downloadingExcel}
                >
                  {downloadingExcel ? (
                    <>
                      <Spinner size="sm" animation="border" className="me-1" /> Excel...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-file-excel me-1"></i> Form B Excel
                    </>
                  )}
                </Button>

                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleDownloadPdf}
                  disabled={loading || downloadingPdf}
                >
                  {downloadingPdf ? (
                    <>
                      <Spinner size="sm" animation="border" className="me-1" /> PDF...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-file-pdf me-1"></i> Dossier PDF
                    </>
                  )}
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* LOADING STATE */}
      {loading && (
        <div className="d-flex flex-column align-items-center justify-content-center py-5">
          <Spinner animation="border" variant="primary" />
          <div className="mt-3 text-muted">Loading Compliance Dossier records...</div>
        </div>
      )}

      {/* MAIN CONTENT TABS */}
      {!loading && dossier && (
        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom p-0">
              <Nav variant="tabs" className="px-3 border-0">
                <Nav.Item>
                  <Nav.Link eventKey="cover" className="py-3 px-4 fw-bold">
                  
                    1. Cover Letter
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="wage" className="py-3 px-4 fw-bold">
              
                    2. Form B (Wage Register)
                    <Badge bg="primary" className="ms-2">
                      {dossier.wageRegister?.length || 0}
                    </Badge>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="muster" className="py-3 px-4 fw-bold">
          
                    3. Form XVI (Muster Roll)
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="challans" className="py-3 px-4 fw-bold">
                    4. EPFO &amp; PT Challans
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="bank" className="py-3 px-4 fw-bold">
                    5. Bank Transfers &amp; UTR
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="staff" className="py-3 px-4 fw-bold">
                    6. Project Staff
                    <Badge bg="secondary" className="ms-2">
                      {dossier.projectStaff?.length || 0}
                    </Badge>
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Card.Header>

            <Card.Body className="p-4" style={{ maxHeight: "75vh", overflowY: "auto" }}>
              <Tab.Content>
                {/* ══════════════════════════════════════════════════════════
                     TAB 1: COVER LETTER & CLIENT DETAILS
                ══════════════════════════════════════════════════════════ */}
                <Tab.Pane eventKey="cover">
                  <Row className="g-4">
                    <Col md={8}>
                      <Card className="border mb-4">
                        <Card.Header className="bg-light border-bottom py-3">
                          <h6 className="mb-0 fw-bold text-dark">
                            <i className="fas fa-building me-2 text-primary"></i> Client &amp; Project Information
                          </h6>
                        </Card.Header>
                        <Card.Body className="p-4">
                          <Row className="g-3">
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label className="fw-semibold mb-1">Client / Principal Employer Name</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={dossier.clientName || ""}
                                  onChange={(e) =>
                                    setDossier((p) => ({ ...p, clientName: e.target.value }))
                                  }
                                  placeholder="Enter client name"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label className="fw-semibold mb-1">Project / Site Name</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={dossier.projectName || ""}
                                  onChange={(e) =>
                                    setDossier((p) => ({ ...p, projectName: e.target.value }))
                                  }
                                  placeholder="Enter project name"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={12}>
                              <Form.Group>
                                <Form.Label className="fw-semibold mb-1">Client Site Address</Form.Label>
                                <Form.Control
                                  as="textarea"
                                  rows={2}
                                  value={dossier.clientAddress || ""}
                                  onChange={(e) =>
                                    setDossier((p) => ({ ...p, clientAddress: e.target.value }))
                                  }
                                  placeholder="Enter complete address"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={4}>
                              <Form.Group>
                                <Form.Label className="fw-semibold mb-1">City</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={dossier.clientCity || ""}
                                  onChange={(e) =>
                                    setDossier((p) => ({ ...p, clientCity: e.target.value }))
                                  }
                                  placeholder="City"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={4}>
                              <Form.Group>
                                <Form.Label className="fw-semibold mb-1">State</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={dossier.clientState || ""}
                                  onChange={(e) =>
                                    setDossier((p) => ({ ...p, clientState: e.target.value }))
                                  }
                                  placeholder="State"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={4}>
                              <Form.Group>
                                <Form.Label className="fw-semibold mb-1">Pincode</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={dossier.clientPincode || ""}
                                  onChange={(e) =>
                                    setDossier((p) => ({ ...p, clientPincode: e.target.value }))
                                  }
                                  placeholder="000000"
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>

                      <Card className="border">
                        <Card.Header className="bg-light border-bottom py-3">
                          <h6 className="mb-0 fw-bold text-dark">
                            <i className="fas fa-envelope me-2 text-primary"></i> Submission Letter Details
                          </h6>
                        </Card.Header>
                        <Card.Body className="p-4">
                          <Row className="g-3">
                            <Col md={4}>
                              <Form.Group>
                                <Form.Label className="fw-semibold mb-1">Submission Date</Form.Label>
                                <Form.Control
                                  type="date"
                                  value={
                                    dossier.submissionDate
                                      ? new Date(dossier.submissionDate).toISOString().split("T")[0]
                                      : ""
                                  }
                                  onChange={(e) =>
                                    setDossier((p) => ({ ...p, submissionDate: e.target.value }))
                                  }
                                />
                              </Form.Group>
                            </Col>
                            <Col md={8}>
                              <Form.Group>
                                <Form.Label className="fw-semibold mb-1">Subject Line</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={dossier.subject || ""}
                                  onChange={(e) =>
                                    setDossier((p) => ({ ...p, subject: e.target.value }))
                                  }
                                  placeholder="Enter subject for cover letter"
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>

                    <Col md={4}>
                      <Card className="border mb-4">
                        <Card.Header className="bg-light border-bottom py-3">
                          <h6 className="mb-0 fw-bold text-dark">
                            <i className="fas fa-file-alt me-2 text-primary"></i> Attached Documents
                          </h6>
                        </Card.Header>
                        <Card.Body className="p-3">
                          <ol className="mb-0 ps-3" style={{ lineHeight: "2.2" }}>
                            <li className="mb-2"><strong>Wage Register (Form B)</strong></li>
                            <li className="mb-2"><strong>Muster Roll (Form XVI)</strong></li>
                            <li className="mb-2"><strong>EPFO Return Statement (ECR)</strong></li>
                            <li className="mb-2"><strong>EPFO Payment Confirmation</strong></li>
                            <li className="mb-2"><strong>Professional Tax Challan</strong></li>
                            <li className="mb-2"><strong>Bank Transfer Proofs</strong></li>
                          </ol>
                        </Card.Body>
                      </Card>

                      <Card className="border">
                        <Card.Header className="bg-light border-bottom py-3">
                          <h6 className="mb-0 fw-bold text-dark">
                            <i className="fas fa-signature me-2 text-primary"></i> Authorized Signatory
                          </h6>
                        </Card.Header>
                        <Card.Body className="p-3">
                          <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold mb-1">Signatory Name</Form.Label>
                            <Form.Control
                              type="text"
                              value={dossier.signatory?.name || ""}
                              onChange={(e) =>
                                setDossier((p) => ({
                                  ...p,
                                  signatory: { ...p.signatory, name: e.target.value }
                                }))
                              }
                              placeholder="e.g. Authorized Signatory Name"
                            />
                          </Form.Group>
                          <Form.Group>
                            <Form.Label className="fw-semibold mb-1">Designation</Form.Label>
                            <Form.Control
                              type="text"
                              value={dossier.signatory?.designation || "Authorized Signatory"}
                              onChange={(e) =>
                                setDossier((p) => ({
                                  ...p,
                                  signatory: { ...p.signatory, designation: e.target.value }
                                }))
                              }
                              placeholder="Designation"
                            />
                          </Form.Group>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Tab.Pane>


                {/* ══════════════════════════════════════════════════════════
                     TAB 2: FORM B — WAGE REGISTER
                ══════════════════════════════════════════════════════════ */}
                <Tab.Pane eventKey="wage">
                  {/* Minimum Wage Rates Config Card */}
                  <Card className="border mb-4">
                    <Card.Header className="bg-light border-bottom py-3">
                      <h6 className="mb-0 fw-bold text-dark">
                        <i className="fas fa-money-bill-wave me-2 text-primary"></i> Minimum Wage Rate Configuration
                      </h6>
                    </Card.Header>
                    <Card.Body className="p-4">
                      <Row className="align-items-center g-3">
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label className="fw-semibold mb-1">Skilled (₹/day)</Form.Label>
                            <Form.Control
                              type="number"
                              value={dossier.minimumWageRates?.skilled || 535}
                              onChange={(e) =>
                                setDossier((p) => ({
                                  ...p,
                                  minimumWageRates: {
                                    ...p.minimumWageRates,
                                    skilled: Number(e.target.value)
                                  }
                                }))
                              }
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label className="fw-semibold mb-1">Semi-Skilled (₹/day)</Form.Label>
                            <Form.Control
                              type="number"
                              value={dossier.minimumWageRates?.semiSkilled || 521}
                              onChange={(e) =>
                                setDossier((p) => ({
                                  ...p,
                                  minimumWageRates: {
                                    ...p.minimumWageRates,
                                    semiSkilled: Number(e.target.value)
                                  }
                                }))
                              }
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label className="fw-semibold mb-1">Unskilled (₹/day)</Form.Label>
                            <Form.Control
                              type="number"
                              value={dossier.minimumWageRates?.unSkilled || 513}
                              onChange={(e) =>
                                setDossier((p) => ({
                                  ...p,
                                  minimumWageRates: {
                                    ...p.minimumWageRates,
                                    unSkilled: Number(e.target.value)
                                  }
                                }))
                              }
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3} className="text-end">
                          <Form.Label className="fw-semibold mb-1 d-block">&nbsp;</Form.Label>
                          <Button variant="success" onClick={openAddWorkerModal} className="w-100">
                            <i className="fas fa-user-plus me-2"></i> Add Worker Row
                          </Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {/* Wage Register Table */}
                  <Card className="border">
                    <Card.Header className="bg-light border-bottom py-3">
                      <h6 className="mb-0 fw-bold text-dark">
                        <i className="fas fa-table me-2 text-primary"></i> Form B - Wage Register Table
                      </h6>
                    </Card.Header>
                    <Card.Body className="p-0">
                      <div className="table-responsive">
                        <Table hover size="sm" className="mb-0 align-middle text-nowrap" style={{ fontSize: "0.85rem" }}>
                          <thead className="table-primary text-center">
                            <tr>
                              <th>#</th>
                              <th>Worker ID</th>
                              <th className="text-start">Worker Name</th>
                              <th>Desig.</th>
                              <th>Rate</th>
                              <th>Days</th>
                              <th>Basic (₹)</th>
                          <th>Gross (₹)</th>
                          <th>PF (₹)</th>
                          <th>ESIC (₹)</th>
                          <th>PT (₹)</th>
                          <th>Adv. (₹)</th>
                          <th>Total Ded. (₹)</th>
                          <th>Net Payable (₹)</th>
                          <th>ER PF (₹)</th>
                          <th>Payment Date</th>
                          <th>UTR / Ref</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(!dossier.wageRegister || dossier.wageRegister.length === 0) ? (
                          <tr>
                            <td colSpan={18} className="text-center py-4 text-muted">
                              No worker wage records found. Click "Add Worker Row" or refresh from Work Order workers.
                            </td>
                          </tr>
                        ) : (
                          dossier.wageRegister.map((w, idx) => (
                            <tr key={idx}>
                              <td className="text-center">{w.serialNo || idx + 1}</td>
                              <td className="text-center font-monospace">{w.workerIdNo || `W-${idx + 1}`}</td>
                              <td className="text-start">
                                <strong>{w.name}</strong>
                                {w.uan && <div className="text-muted small">UAN: {w.uan}</div>}
                              </td>
                              <td className="text-center">{w.designation}</td>
                              <td className="text-end">₹{w.rateOfWage}</td>
                              <td className="text-center fw-bold">{w.daysWorked}</td>
                              <td className="text-end">₹{Number(w.basic || 0).toLocaleString("en-IN")}</td>
                              <td className="text-end fw-bold text-dark">₹{Number(w.grossWages || 0).toLocaleString("en-IN")}</td>
                              <td className="text-end text-danger">₹{Number(w.pfEmployee || 0).toLocaleString("en-IN")}</td>
                              <td className="text-end text-danger">₹{Number(w.esicEmployee || 0).toLocaleString("en-IN")}</td>
                              <td className="text-end text-danger">₹{Number(w.pt || 0).toLocaleString("en-IN")}</td>
                              <td className="text-end text-danger">₹{Number(w.advance || 0).toLocaleString("en-IN")}</td>
                              <td className="text-end fw-bold text-danger">₹{Number(w.totalDeductions || 0).toLocaleString("en-IN")}</td>
                              <td className="text-end fw-bold text-success" style={{ fontSize: "0.85rem" }}>
                                ₹{Number(w.netPayable || 0).toLocaleString("en-IN")}
                              </td>
                              <td className="text-end">₹{Number(w.employerPfShare || 0).toLocaleString("en-IN")}</td>
                              <td className="text-center">
                                {w.paymentDate ? new Date(w.paymentDate).toLocaleDateString("en-IN") : "-"}
                              </td>
                              <td className="text-center small font-monospace">
                                {w.utrNumber || <Badge bg="light" text="dark">Bank Transfer</Badge>}
                              </td>
                              <td className="text-center">
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0 me-2 text-primary"
                                  onClick={() => openEditWorkerModal(w, idx)}
                                >
                                  <i className="fas fa-edit"></i>
                                </Button>
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0 text-danger"
                                  onClick={() => removeWorkerItem(idx)}
                                >
                                  <i className="fas fa-trash"></i>
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                        {/* TOTAL ROW */}
                        <tr className="table-secondary fw-bold">
                          <td colSpan={5} className="text-center">TOTAL ({dossier.wageRegister?.length || 0} Workers)</td>
                          <td className="text-center">{totals.daysWorked}</td>
                          <td className="text-end">-</td>
                          <td className="text-end text-dark">₹{totals.grossWages.toLocaleString("en-IN")}</td>
                          <td className="text-end text-danger">₹{totals.pfEmployee.toLocaleString("en-IN")}</td>
                          <td className="text-end text-danger">₹{totals.esicEmployee.toLocaleString("en-IN")}</td>
                          <td className="text-end text-danger">₹{totals.pt.toLocaleString("en-IN")}</td>
                          <td className="text-end text-danger">₹{totals.advance.toLocaleString("en-IN")}</td>
                          <td className="text-end text-danger">₹{totals.totalDeductions.toLocaleString("en-IN")}</td>
                          <td className="text-end text-success" style={{ fontSize: "0.9rem" }}>
                            ₹{totals.netPayable.toLocaleString("en-IN")}
                          </td>
                          <td className="text-end">₹{totals.employerPfShare.toLocaleString("en-IN")}</td>
                          <td colSpan={3}></td>
                        </tr>
                      </tbody>
                    </Table>
                      </div>
                    </Card.Body>
                  </Card>
                </Tab.Pane>


                {/* ══════════════════════════════════════════════════════════
                     TAB 3: FORM XVI — MUSTER ROLL
                ══════════════════════════════════════════════════════════ */}
                <Tab.Pane eventKey="muster">
                  <Card className="border">
                    <Card.Header className="bg-light border-bottom py-3">
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <h6 className="mb-0 fw-bold text-dark">
                          <i className="fas fa-calendar-check me-2 text-primary"></i> Form XVI - Muster Roll (Daily Attendance 1 to 31)
                        </h6>
                        <div>
                          <Badge bg="success" className="me-1 px-2 py-1">P : Present</Badge>
                          <Badge bg="secondary" className="me-1 px-2 py-1">WO : Weekly Off</Badge>
                          <Badge bg="danger" className="px-2 py-1">A : Absent</Badge>
                        </div>
                      </div>
                    </Card.Header>
                    <Card.Body className="p-0">
                      <div className="table-responsive">
                        <Table bordered size="sm" className="mb-0 text-center text-nowrap" style={{ fontSize: "0.78rem" }}>
                          <thead className="table-dark">
                            <tr>
                              <th>#</th>
                              <th className="text-start" style={{ minWidth: "150px" }}>Worker Name</th>
                              <th>Desig.</th>
                              {Array.from({ length: 31 }, (_, i) => (
                                <th key={i} style={{ width: "26px", padding: "4px" }}>{i + 1}</th>
                              ))}
                              <th className="bg-warning text-dark">Total Days</th>
                            </tr>
                          </thead>
                      <tbody>
                        {(!dossier.musterRoll || dossier.musterRoll.length === 0) ? (
                          <tr>
                            <td colSpan={35} className="py-4 text-muted">No muster roll records found.</td>
                          </tr>
                        ) : (
                          dossier.musterRoll.map((m, idx) => {
                            const att = m.dailyAttendance || {};
                            return (
                              <tr key={idx}>
                                <td>{m.serialNo || idx + 1}</td>
                                <td className="text-start fw-bold">{m.name}</td>
                                <td>{m.designation}</td>
                                {Array.from({ length: 31 }, (_, i) => {
                                  const dayKey = String(i + 1);
                                  const val = att[dayKey] || (i < (m.totalDaysWorked || 26) ? (i % 7 === 6 ? "WO" : "P") : "A");
                                  const bgCol = val === "WO" ? "#eef" : val === "A" ? "#fee" : "#efe";
                                  const textCol = val === "A" ? "red" : val === "WO" ? "#555" : "green";
                                  return (
                                    <td
                                      key={i}
                                      style={{ backgroundColor: bgCol, color: textCol, fontWeight: "bold", padding: "2px" }}
                                    >
                                      {val}
                                    </td>
                                  );
                                })}
                                <td className="fw-bold bg-light">{m.totalDaysWorked || 0}</td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                      </div>
                    </Card.Body>
                  </Card>
                </Tab.Pane>


                {/* ══════════════════════════════════════════════════════════
                     TAB 4: EPFO & PT CHALLANS
                ══════════════════════════════════════════════════════════ */}
                <Tab.Pane eventKey="challans">
                  <Row className="g-4">
                    {/* EPFO SECTION */}
                    <Col md={6}>
                      <Card className="border h-100">
                        <Card.Header className="bg-light border-bottom py-3">
                          <h6 className="mb-0 fw-bold text-dark">
                            <i className="fas fa-shield-alt me-2 text-primary"></i> EPFO / ECR Details &amp; Challans
                          </h6>
                        </Card.Header>
                        <Card.Body className="p-4">
                          <Row className="g-3">
                            <Col md={12}>
                              <Form.Group>
                                <Form.Label className="fw-semibold mb-1">Establishment Code</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={dossier.epfo?.establishmentCode || "THVSH1554512000"}
                                  onChange={(e) =>
                                    setDossier((p) => ({
                                      ...p,
                                      epfo: { ...p.epfo, establishmentCode: e.target.value }
                                    }))
                                  }
                                  placeholder="Establishment Code"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={12}>
                              <Form.Group>
                                <Form.Label className="fw-semibold mb-1">TRRN Number</Form.Label>
                                <Form.Control
                                  type="text"
                                  placeholder="e.g. 2605319132632"
                                  value={dossier.epfo?.trrn || ""}
                                  onChange={(e) =>
                                    setDossier((p) => ({
                                      ...p,
                                      epfo: { ...p.epfo, trrn: e.target.value }
                                    }))
                                  }
                                />
                              </Form.Group>
                            </Col>
                            <Col md={12}>
                              <Form.Group>
                                <Form.Label className="fw-semibold mb-1">Return File ID</Form.Label>
                                <Form.Control
                                  type="text"
                                  placeholder="e.g. 260512683396"
                                  value={dossier.epfo?.returnFileId || ""}
                                  onChange={(e) =>
                                    setDossier((p) => ({
                                      ...p,
                                      epfo: { ...p.epfo, returnFileId: e.target.value }
                                    }))
                                  }
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label className="fw-semibold mb-1">Total Members</Form.Label>
                                <Form.Control
                                  type="number"
                                  value={dossier.epfo?.totalMembers || dossier.wageRegister?.length || 0}
                                  onChange={(e) =>
                                    setDossier((p) => ({
                                      ...p,
                                      epfo: { ...p.epfo, totalMembers: Number(e.target.value) }
                                    }))
                                  }
                                />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label className="fw-semibold mb-1">Total Paid (₹)</Form.Label>
                                <Form.Control
                                  type="number"
                                  value={dossier.epfo?.totalChallanAmount || (totals.pfEmployee + totals.employerPfShare)}
                                  onChange={(e) =>
                                    setDossier((p) => ({
                                      ...p,
                                      epfo: { ...p.epfo, totalChallanAmount: Number(e.target.value) }
                                    }))
                                  }
                                />
                              </Form.Group>
                            </Col>
                            <Col md={12}>
                              <Form.Group>
                                <Form.Label className="fw-semibold mb-1">Payment Bank / CRN</Form.Label>
                                <Form.Control
                                  type="text"
                                  placeholder="Union Bank / 026120526470237"
                                  value={dossier.epfo?.crn || ""}
                                  onChange={(e) =>
                                    setDossier((p) => ({
                                      ...p,
                                      epfo: { ...p.epfo, crn: e.target.value }
                                    }))
                                  }
                                />
                              </Form.Group>
                            </Col>
                          </Row>

                          <hr className="my-3" />
                          <div className="fw-bold mb-2 text-muted small">Upload EPFO Documents</div>
                          <div className="d-flex gap-2 flex-wrap">
                            <label className="btn btn-outline-primary btn-sm mb-0">
                              <i className="fas fa-upload me-2"></i> Attach ECR PDF
                              <input
                                type="file"
                                accept="application/pdf,image/*"
                                hidden
                                onChange={(e) => handleFileUpload(e, "ECR", "EPFO ECR Statement")}
                              />
                            </label>

                            <label className="btn btn-outline-success btn-sm mb-0">
                              <i className="fas fa-receipt me-2"></i> Attach Paid Receipt
                              <input
                                type="file"
                                accept="application/pdf,image/*"
                                hidden
                                onChange={(e) => handleFileUpload(e, "PF_PAID_CHALLAN", "EPFO Paid Receipt")}
                              />
                            </label>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>

                    {/* PT SECTION */}
                    <Col md={6}>
                      <Card className="border h-100">
                        <Card.Header className="bg-light border-bottom py-3">
                          <h6 className="mb-0 fw-bold text-dark">
                            <i className="fas fa-landmark me-2 text-primary"></i> Professional Tax (PT) Details
                          </h6>
                        </Card.Header>
                        <Card.Body className="p-4">
                          <Row className="g-3">
                            <Col md={12}>
                              <Form.Group>
                                <Form.Label className="fw-semibold mb-1">PTRC Tax ID</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={dossier.pt?.ptrcTaxId || "27571235657P"}
                                  onChange={(e) =>
                                    setDossier((p) => ({
                                      ...p,
                                      pt: { ...p.pt, ptrcTaxId: e.target.value }
                                    }))
                                  }
                                  placeholder="PTRC ID"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={12}>
                              <Form.Group>
                                <Form.Label className="fw-semibold mb-1">GRN Number</Form.Label>
                                <Form.Control
                                  type="text"
                                  placeholder="e.g. MH002431328202627E"
                                  value={dossier.pt?.grnNumber || ""}
                                  onChange={(e) =>
                                    setDossier((p) => ({
                                      ...p,
                                      pt: { ...p.pt, grnNumber: e.target.value }
                                    }))
                                  }
                                />
                              </Form.Group>
                            </Col>
                            <Col md={12}>
                              <Form.Group>
                                <Form.Label className="fw-semibold mb-1">Sales Tax Office</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={dossier.pt?.officeName || "Sales Tax Office RAIGAD"}
                                  onChange={(e) =>
                                    setDossier((p) => ({
                                      ...p,
                                      pt: { ...p.pt, officeName: e.target.value }
                                    }))
                                  }
                                  placeholder="Office Name"
                                />
                              </Form.Group>
                            </Col>
                            <Col md={12}>
                              <Form.Group>
                                <Form.Label className="fw-semibold mb-1">Total PT Paid (₹)</Form.Label>
                                <Form.Control
                                  type="number"
                                  value={dossier.pt?.totalAmount || totals.pt}
                                  onChange={(e) =>
                                    setDossier((p) => ({
                                      ...p,
                                      pt: { ...p.pt, totalAmount: Number(e.target.value) }
                                    }))
                                  }
                                />
                              </Form.Group>
                            </Col>
                          </Row>

                          <hr className="my-3" />
                          <div className="fw-bold mb-2 text-muted small">Upload PT Challan</div>
                          <label className="btn btn-outline-dark btn-sm mb-0 w-100">
                            <i className="fas fa-upload me-2"></i> Attach PT Challan (Form III_B)
                            <input
                              type="file"
                              accept="application/pdf,image/*"
                              hidden
                              onChange={(e) => handleFileUpload(e, "PT_PAID_CHALLAN", "PT Paid Challan MTR-6")}
                            />
                          </label>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  {/* LIST OF ATTACHED DOCUMENTS */}
                  {dossier.attachedDocuments?.length > 0 && (
                    <Card className="border mt-4">
                      <Card.Header className="bg-light fw-bold">
                        <i className="fas fa-paperclip me-2"></i> Uploaded Challans &amp; Receipts ({dossier.attachedDocuments.length})
                      </Card.Header>
                      <Card.Body className="p-0">
                        <Table hover size="sm" className="mb-0 align-middle">
                          <thead>
                            <tr>
                              <th>Type</th>
                              <th>Document Title</th>
                              <th>Uploaded At</th>
                              <th className="text-center">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dossier.attachedDocuments.map((doc, idx) => (
                              <tr key={idx}>
                                <td><Badge bg="info">{doc.docType}</Badge></td>
                                <td>{doc.title || doc.fileName}</td>
                                <td>{new Date(doc.uploadedAt).toLocaleString("en-IN")}</td>
                                <td className="text-center">
                                  <a
                                    href={getFileUrl(doc.fileUrl)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="btn btn-outline-primary btn-sm py-0 px-2 me-2"
                                  >
                                    <i className="fas fa-eye me-1"></i> View
                                  </a>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    className="py-0 px-2"
                                    onClick={() => handleRemoveDoc(idx)}
                                  >
                                    <i className="fas fa-trash me-1"></i> Delete
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  )}
                </Tab.Pane>


                {/* ══════════════════════════════════════════════════════════
                     TAB 5: BANK TRANSFERS & UTR PROOFS
                ══════════════════════════════════════════════════════════ */}
                <Tab.Pane eventKey="bank">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="fw-bold text-primary mb-0">
                      <i className="fas fa-money-bill-wave me-2"></i> Worker Bank Transfer &amp; UTR Details
                    </h6>
                    <div>
                      <label className="btn btn-outline-primary btn-sm mb-0 me-2">
                        <i className="fas fa-upload me-1"></i> Attach SBI Bank Statement
                        <input
                          type="file"
                          accept="application/pdf,image/*"
                          hidden
                          onChange={(e) => handleFileUpload(e, "BANK_STATEMENT", "Bank Statement")}
                        />
                      </label>
                      <label className="btn btn-outline-info btn-sm mb-0">
                        <i className="fas fa-file-invoice me-1"></i> Attach e-PayOrder Slips
                        <input
                          type="file"
                          accept="application/pdf,image/*"
                          hidden
                          onChange={(e) => handleFileUpload(e, "PAYMENT_SLIP", "e-PayOrder Slip")}
                        />
                      </label>
                    </div>
                  </div>

                  <div className="table-responsive border rounded">
                    <Table hover size="sm" className="mb-0 align-middle text-nowrap">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Worker Name</th>
                          <th>Net Amount (₹)</th>
                          <th>Bank Name</th>
                          <th>Account Number</th>
                          <th>UTR / Journal Ref Number</th>
                          <th>Payment Date</th>
                          <th>Debit Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dossier.wageRegister?.map((w, idx) => (
                          <tr key={idx}>
                            <td>{idx + 1}</td>
                            <td className="fw-bold">{w.name}</td>
                            <td className="text-success fw-bold">₹{Number(w.netPayable || 0).toLocaleString("en-IN")}</td>
                            <td>
                              <Form.Control
                                type="text"
                                size="sm"
                                style={{ width: "160px" }}
                                value={w.bankName || "State Bank of India"}
                                onChange={(e) => {
                                  const list = [...dossier.wageRegister];
                                  list[idx].bankName = e.target.value;
                                  setDossier((p) => ({ ...p, wageRegister: list }));
                                }}
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="text"
                                size="sm"
                                style={{ width: "160px" }}
                                placeholder="00000041049245791"
                                value={w.bankAccountNo || ""}
                                onChange={(e) => {
                                  const list = [...dossier.wageRegister];
                                  list[idx].bankAccountNo = e.target.value;
                                  setDossier((p) => ({ ...p, wageRegister: list }));
                                }}
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="text"
                                size="sm"
                                style={{ width: "180px" }}
                                placeholder="e.g. CNAFDLSEI3 / SBIN326..."
                                value={w.utrNumber || ""}
                                onChange={(e) => {
                                  const list = [...dossier.wageRegister];
                                  list[idx].utrNumber = e.target.value;
                                  setDossier((p) => ({ ...p, wageRegister: list }));
                                }}
                              />
                            </td>
                            <td>
                              <Form.Control
                                type="date"
                                size="sm"
                                value={
                                  w.paymentDate
                                    ? new Date(w.paymentDate).toISOString().split("T")[0]
                                    : ""
                                }
                                onChange={(e) => {
                                  const list = [...dossier.wageRegister];
                                  list[idx].paymentDate = e.target.value;
                                  setDossier((p) => ({ ...p, wageRegister: list }));
                                }}
                              />
                            </td>
                            <td>
                              <Badge bg="success">Success</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Tab.Pane>

                {/* ══════════════════════════════════════════════════════════
                     TAB 6: PROJECT STAFF
                ══════════════════════════════════════════════════════════ */}
                <Tab.Pane eventKey="staff">
                  <Card className="border mb-4">
                    <Card.Header className="bg-light border-bottom py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <div>
                        <h6 className="mb-0 fw-bold text-dark">
                          <i className="fas fa-users me-2 text-primary"></i> Project Staff Management
                        </h6>
                        <small className="text-muted">
                          Staff assigned to this Work Order and its Tasks
                        </small>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={handleAutoFetchStaff}
                          disabled={fetchingStaff}
                        >
                          {fetchingStaff ? (
                            <>
                              <Spinner size="sm" animation="border" className="me-1" /> Fetching...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-bolt me-1 text-warning"></i> Auto-Fetch from Tasks &amp; WO
                            </>
                          )}
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => {
                            const newStaff = {
                              name: "",
                              designation: "",
                              department: "",
                              contactNumber: "",
                              email: "",
                              joiningDate: "",
                              monthlySalary: 0,
                              responsibilities: "",
                              uan: "",
                              pfNumber: "",
                              pfEmployee: 0,
                              employerPfShare: 0,
                              pt: 0,
                              netPayable: 0
                            };
                            setDossier((p) => ({
                              ...p,
                              projectStaff: [...(p.projectStaff || []), newStaff]
                            }));
                          }}
                        >
                          <i className="fas fa-user-plus me-1"></i> Add Staff Member
                        </Button>
                      </div>
                    </Card.Header>
                    <Card.Body className="p-4">
                      {(!dossier.projectStaff || dossier.projectStaff.length === 0) ? (
                        <div className="text-center py-5 text-muted">
                          <i className="fas fa-user-friends fa-3x mb-3 opacity-25"></i>
                          <p className="mb-2 fw-semibold">No staff members added yet.</p>
                          <p className="mb-3 small">
                            You can automatically pull staff members who have assigned tasks or roles under this Work Order, or add them manually.
                          </p>
                          <div className="d-flex justify-content-center gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={handleAutoFetchStaff}
                              disabled={fetchingStaff}
                            >
                              <i className="fas fa-bolt me-1 text-warning"></i> Auto-Fetch from Tasks &amp; WO
                            </Button>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => {
                                const newStaff = {
                                  name: "",
                                  designation: "",
                                  department: "",
                                  contactNumber: "",
                                  email: "",
                                  joiningDate: "",
                                  monthlySalary: 0,
                                  responsibilities: "",
                                  uan: "",
                                  pfNumber: "",
                                  pfEmployee: 0,
                                  employerPfShare: 0,
                                  pt: 0,
                                  netPayable: 0
                                };
                                setDossier((p) => ({
                                  ...p,
                                  projectStaff: [newStaff]
                                }));
                              }}
                            >
                              <i className="fas fa-user-plus me-1"></i> Add Manually
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Row className="g-4">
                          {dossier.projectStaff.map((staff, idx) => (
                            <Col md={6} key={idx}>
                              <Card className="border h-100">
                                <Card.Header className="bg-light d-flex justify-content-between align-items-center py-2">
                                  <span className="fw-bold">Staff Member #{idx + 1}</span>
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="text-danger p-0"
                                    onClick={() => {
                                      const list = [...dossier.projectStaff];
                                      list.splice(idx, 1);
                                      setDossier((p) => ({ ...p, projectStaff: list }));
                                    }}
                                  >
                                    <i className="fas fa-trash"></i>
                                  </Button>
                                </Card.Header>
                                <Card.Body className="p-3">
                                  <Row className="g-3">
                                    <Col md={12}>
                                      <Form.Group>
                                        <Form.Label className="fw-semibold mb-1 small">Full Name</Form.Label>
                                        <Form.Control
                                          type="text"
                                          size="sm"
                                          placeholder="Enter staff name"
                                          value={staff.name || ""}
                                          onChange={(e) => {
                                            const list = [...dossier.projectStaff];
                                            list[idx].name = e.target.value;
                                            setDossier((p) => ({ ...p, projectStaff: list }));
                                          }}
                                        />
                                      </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                      <Form.Group>
                                        <Form.Label className="fw-semibold mb-1 small">Designation</Form.Label>
                                        <Form.Control
                                          type="text"
                                          size="sm"
                                          placeholder="e.g. Site Engineer"
                                          value={staff.designation || ""}
                                          onChange={(e) => {
                                            const list = [...dossier.projectStaff];
                                            list[idx].designation = e.target.value;
                                            setDossier((p) => ({ ...p, projectStaff: list }));
                                          }}
                                        />
                                      </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                      <Form.Group>
                                        <Form.Label className="fw-semibold mb-1 small">Department</Form.Label>
                                        <Form.Select
                                          size="sm"
                                          value={staff.department || ""}
                                          onChange={(e) => {
                                            const list = [...dossier.projectStaff];
                                            list[idx].department = e.target.value;
                                            setDossier((p) => ({ ...p, projectStaff: list }));
                                          }}
                                        >
                                          <option value="">Select Department</option>
                                          <option value="Engineering">Engineering</option>
                                          <option value="Administration">Administration</option>
                                          <option value="Accounts">Accounts</option>
                                          <option value="HR">HR</option>
                                          <option value="Safety">Safety</option>
                                          <option value="Quality">Quality</option>
                                          <option value="Other">Other</option>
                                        </Form.Select>
                                      </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                      <Form.Group>
                                        <Form.Label className="fw-semibold mb-1 small">Contact Number</Form.Label>
                                        <Form.Control
                                          type="tel"
                                          size="sm"
                                          placeholder="Mobile number"
                                          value={staff.contactNumber || ""}
                                          onChange={(e) => {
                                            const list = [...dossier.projectStaff];
                                            list[idx].contactNumber = e.target.value;
                                            setDossier((p) => ({ ...p, projectStaff: list }));
                                          }}
                                        />
                                      </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                      <Form.Group>
                                        <Form.Label className="fw-semibold mb-1 small">Email</Form.Label>
                                        <Form.Control
                                          type="email"
                                          size="sm"
                                          placeholder="Email address"
                                          value={staff.email || ""}
                                          onChange={(e) => {
                                            const list = [...dossier.projectStaff];
                                            list[idx].email = e.target.value;
                                            setDossier((p) => ({ ...p, projectStaff: list }));
                                          }}
                                        />
                                      </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                      <Form.Group>
                                        <Form.Label className="fw-semibold mb-1 small">Joining Date</Form.Label>
                                        <Form.Control
                                          type="date"
                                          size="sm"
                                          value={
                                            staff.joiningDate
                                              ? new Date(staff.joiningDate).toISOString().split("T")[0]
                                              : ""
                                          }
                                          onChange={(e) => {
                                            const list = [...dossier.projectStaff];
                                            list[idx].joiningDate = e.target.value;
                                            setDossier((p) => ({ ...p, projectStaff: list }));
                                          }}
                                        />
                                      </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                      <Form.Group>
                                        <Form.Label className="fw-semibold mb-1 small">UAN (PF Number)</Form.Label>
                                        <Form.Control
                                          type="text"
                                          size="sm"
                                          placeholder="101xxxxxxxx"
                                          value={staff.uan || ""}
                                          onChange={(e) => {
                                            const list = [...dossier.projectStaff];
                                            list[idx].uan = e.target.value;
                                            setDossier((p) => ({ ...p, projectStaff: list }));
                                          }}
                                        />
                                      </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                      <Form.Group>
                                        <Form.Label className="fw-semibold mb-1 small">Monthly Gross (₹)</Form.Label>
                                        <Form.Control
                                          type="number"
                                          size="sm"
                                          placeholder="0"
                                          value={staff.monthlySalary || ""}
                                          onChange={(e) => {
                                            const list = [...dossier.projectStaff];
                                            const sal = Number(e.target.value);
                                            const pf = sal >= 15000 ? 1800 : Math.round(sal * 0.12);
                                            const pt = sal > 10000 ? 200 : 0;
                                            const net = sal - (pf + pt);
                                            list[idx].monthlySalary = sal;
                                            list[idx].pfEmployee = pf;
                                            list[idx].employerPfShare = pf;
                                            list[idx].pt = pt;
                                            list[idx].netPayable = net;
                                            setDossier((p) => ({ ...p, projectStaff: list }));
                                          }}
                                        />
                                      </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                      <Form.Group>
                                        <Form.Label className="fw-semibold mb-1 small">PF Deduction (₹)</Form.Label>
                                        <Form.Control
                                          type="number"
                                          size="sm"
                                          placeholder="0"
                                          value={staff.pfEmployee || ""}
                                          onChange={(e) => {
                                            const list = [...dossier.projectStaff];
                                            const pf = Number(e.target.value);
                                            list[idx].pfEmployee = pf;
                                            list[idx].employerPfShare = pf;
                                            list[idx].netPayable = (list[idx].monthlySalary || 0) - (pf + (list[idx].pt || 0));
                                            setDossier((p) => ({ ...p, projectStaff: list }));
                                          }}
                                        />
                                      </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                      <Form.Group>
                                        <Form.Label className="fw-semibold mb-1 small">PT (₹)</Form.Label>
                                        <Form.Control
                                          type="number"
                                          size="sm"
                                          placeholder="200"
                                          value={staff.pt || ""}
                                          onChange={(e) => {
                                            const list = [...dossier.projectStaff];
                                            const pt = Number(e.target.value);
                                            list[idx].pt = pt;
                                            list[idx].netPayable = (list[idx].monthlySalary || 0) - ((list[idx].pfEmployee || 0) + pt);
                                            setDossier((p) => ({ ...p, projectStaff: list }));
                                          }}
                                        />
                                      </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                      <Form.Group>
                                        <Form.Label className="fw-semibold mb-1 small text-success">Net Pay (₹)</Form.Label>
                                        <Form.Control
                                          type="number"
                                          size="sm"
                                          className="fw-bold text-success"
                                          placeholder="0"
                                          value={staff.netPayable || ""}
                                          onChange={(e) => {
                                            const list = [...dossier.projectStaff];
                                            list[idx].netPayable = Number(e.target.value);
                                            setDossier((p) => ({ ...p, projectStaff: list }));
                                          }}
                                        />
                                      </Form.Group>
                                    </Col>
                                    <Col md={12}>
                                      <Form.Group>
                                        <Form.Label className="fw-semibold mb-1 small">Responsibilities</Form.Label>
                                        <Form.Control
                                          as="textarea"
                                          rows={2}
                                          size="sm"
                                          placeholder="Key responsibilities and duties"
                                          value={staff.responsibilities || ""}
                                          onChange={(e) => {
                                            const list = [...dossier.projectStaff];
                                            list[idx].responsibilities = e.target.value;
                                            setDossier((p) => ({ ...p, projectStaff: list }));
                                          }}
                                        />
                                      </Form.Group>
                                    </Col>
                                  </Row>
                                </Card.Body>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      )}
                    </Card.Body>
                  </Card>
                </Tab.Pane>
              </Tab.Content>
            </Card.Body>
          </Card>
        </Tab.Container>
      )}
      </>
      )}

      {/* ADD / EDIT WORKER MODAL */}
      <Modal show={showWorkerModal} onHide={() => setShowWorkerModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title className="h6 fw-bold text-primary">
            {editingWorkerIndex !== null ? "Edit Worker Wage Entry" : "Add Worker Wage Entry"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Label className="small fw-semibold">Worker Name *</Form.Label>
              <Form.Control
                type="text"
                size="sm"
                value={workerFormData.name}
                onChange={(e) => setWorkerFormData({ ...workerFormData, name: e.target.value })}
              />
            </Col>
            <Col md={3}>
              <Form.Label className="small fw-semibold">Worker ID / S.No</Form.Label>
              <Form.Control
                type="text"
                size="sm"
                value={workerFormData.workerIdNo}
                onChange={(e) => setWorkerFormData({ ...workerFormData, workerIdNo: e.target.value })}
              />
            </Col>
            <Col md={3}>
              <Form.Label className="small fw-semibold">UAN</Form.Label>
              <Form.Control
                type="text"
                size="sm"
                placeholder="101xxxxxxxx"
                value={workerFormData.uan || ""}
                onChange={(e) => setWorkerFormData({ ...workerFormData, uan: e.target.value })}
              />
            </Col>
            <Col md={4}>
              <Form.Label className="small fw-semibold">Designation</Form.Label>
              <Form.Select
                size="sm"
                value={workerFormData.designation}
                onChange={(e) => setWorkerFormData({ ...workerFormData, designation: e.target.value })}
              >
                <option value="Helper">Helper</option>
                <option value="Electrician">Electrician</option>
                <option value="Fitter">Fitter</option>
                <option value="Welder">Welder</option>
                <option value="Mason">Mason</option>
                <option value="Supervisor">Supervisor</option>
                <option value="Site Engineer">Site Engineer</option>
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label className="small fw-semibold">Daily Rate (₹)</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                value={workerFormData.rateOfWage}
                onChange={(e) => {
                  const rate = Number(e.target.value);
                  const days = Number(workerFormData.daysWorked);
                  const basic = Math.round(rate * days);
                  const gross = basic;
                  const pf = basic >= 15000 ? 1800 : Math.round(basic * 0.12);
                  const pt = gross > 10000 ? 200 : 0;
                  const ded = pf + pt + Number(workerFormData.advance || 0);
                  const net = gross - ded;
                  setWorkerFormData({
                    ...workerFormData,
                    rateOfWage: rate,
                    basic,
                    grossWages: gross,
                    pfEmployee: pf,
                    employerPfShare: pf,
                    pt,
                    totalDeductions: ded,
                    netPayable: net
                  });
                }}
              />
            </Col>
            <Col md={4}>
              <Form.Label className="small fw-semibold">Days Worked</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                value={workerFormData.daysWorked}
                onChange={(e) => {
                  const days = Number(e.target.value);
                  const rate = Number(workerFormData.rateOfWage);
                  const basic = Math.round(rate * days);
                  const gross = basic;
                  const pf = basic >= 15000 ? 1800 : Math.round(basic * 0.12);
                  const pt = gross > 10000 ? 200 : 0;
                  const ded = pf + pt + Number(workerFormData.advance || 0);
                  const net = gross - ded;
                  setWorkerFormData({
                    ...workerFormData,
                    daysWorked: days,
                    basic,
                    grossWages: gross,
                    pfEmployee: pf,
                    employerPfShare: pf,
                    pt,
                    totalDeductions: ded,
                    netPayable: net
                  });
                }}
              />
            </Col>

            <Col md={3}>
              <Form.Label className="small fw-semibold">Gross Wages (₹)</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                value={workerFormData.grossWages}
                onChange={(e) => {
                  const gross = Number(e.target.value);
                  const net = gross - Number(workerFormData.totalDeductions || 0);
                  setWorkerFormData({ ...workerFormData, grossWages: gross, netPayable: net });
                }}
              />
            </Col>
            <Col md={3}>
              <Form.Label className="small fw-semibold">PF Deduction (₹)</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                value={workerFormData.pfEmployee}
                onChange={(e) => {
                  const pf = Number(e.target.value);
                  const ded = pf + Number(workerFormData.pt || 0) + Number(workerFormData.advance || 0);
                  const net = Number(workerFormData.grossWages) - ded;
                  setWorkerFormData({ ...workerFormData, pfEmployee: pf, employerPfShare: pf, totalDeductions: ded, netPayable: net });
                }}
              />
            </Col>
            <Col md={3}>
              <Form.Label className="small fw-semibold">PT Deduction (₹)</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                value={workerFormData.pt}
                onChange={(e) => {
                  const pt = Number(e.target.value);
                  const ded = Number(workerFormData.pfEmployee || 0) + pt + Number(workerFormData.advance || 0);
                  const net = Number(workerFormData.grossWages) - ded;
                  setWorkerFormData({ ...workerFormData, pt, totalDeductions: ded, netPayable: net });
                }}
              />
            </Col>
            <Col md={3}>
              <Form.Label className="small fw-semibold">Advance (₹)</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                value={workerFormData.advance}
                onChange={(e) => {
                  const adv = Number(e.target.value);
                  const ded = Number(workerFormData.pfEmployee || 0) + Number(workerFormData.pt || 0) + adv;
                  const net = Number(workerFormData.grossWages) - ded;
                  setWorkerFormData({ ...workerFormData, advance: adv, totalDeductions: ded, netPayable: net });
                }}
              />
            </Col>

            <Col md={4}>
              <Form.Label className="small fw-semibold text-success">Net Payable (₹)</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                className="fw-bold text-success"
                value={workerFormData.netPayable}
                onChange={(e) => setWorkerFormData({ ...workerFormData, netPayable: Number(e.target.value) })}
              />
            </Col>
            <Col md={4}>
              <Form.Label className="small fw-semibold">Bank UTR / Ref</Form.Label>
              <Form.Control
                type="text"
                size="sm"
                placeholder="CNAFDL..."
                value={workerFormData.utrNumber || ""}
                onChange={(e) => setWorkerFormData({ ...workerFormData, utrNumber: e.target.value })}
              />
            </Col>
            <Col md={4}>
              <Form.Label className="small fw-semibold">Payment Date</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={workerFormData.paymentDate ? new Date(workerFormData.paymentDate).toISOString().split("T")[0] : ""}
                onChange={(e) => setWorkerFormData({ ...workerFormData, paymentDate: e.target.value })}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowWorkerModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleWorkerFormSave}>
            Save Worker
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default ComplianceDossierPage;
